import React, { useEffect, useState } from "react";
import api, { fileUrl, formatPrice } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Landmark, ChevronDown, MessageCircle, Globe, Star, Calculator } from "lucide-react";

const rateVal = (p) => { const m = String(p.interest_rate || "").match(/\d+(?:\.\d+)?/); return m ? parseFloat(m[0]) : Infinity; };
const sortProviders = (arr) => [...arr].sort((a, b) => { const fa = a.featured ? 1 : 0, fb = b.featured ? 1 : 0; if (fa !== fb) return fb - fa; return rateVal(a) - rateVal(b); });

export default function SidebarLoanOptions({ property }) {
  const { feature } = useSettings();
  const [providers, setProviders] = useState([]);
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [emiAmt, setEmiAmt] = useState(() => (property?.price ? String(Math.round(property.price * 0.8)) : ""));
  const [emiRate, setEmiRate] = useState("8.5");
  const [emiYears, setEmiYears] = useState("20");
  const isSale = property?.listing_type === "sale";

  useEffect(() => {
    if (isSale && feature("home_loans")) api.get("/loan-providers").then((r) => setProviders(r.data.providers || [])).catch(() => {});
  }, [isSale, feature]);

  if (!isSale || !feature("home_loans")) return null;

  const unique = Array.from(new Map(providers.map((p) => [p.id, p])).values());
  const pinnedIds = property?.pinned_loan_providers || [];
  const pinnedMatch = pinnedIds.length ? unique.filter((p) => pinnedIds.includes(p.id)) : [];
  // Fallback: if pins are stale (provider unapproved/removed) show all approved providers
  const base = pinnedMatch.length ? pinnedMatch : unique;
  const sorted = sortProviders(base);
  if (sorted.length === 0) return null;
  const usePaging = pinnedMatch.length === 0 && sorted.length > 3;
  const list = usePaging && !showAll ? sorted.slice(0, 3) : sorted;

  const P = parseFloat(emiAmt) || 0;
  const r = (parseFloat(emiRate) || 0) / 12 / 100;
  const n = (parseFloat(emiYears) || 0) * 12;
  const emi = P && r && n ? (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : 0;

  const enquire = (p) => {
    const num = (p.contact || "").replace(/[^0-9]/g, "");
    if (!num) return;
    const text = `Hi, I'm interested in a home loan for the property "${property.title}" (ID: ${property.id}). Please share details.`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <div className="bg-card border border-brand-line rounded-2xl p-5" data-testid="sidebar-loan-options">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3" data-testid="sidebar-loan-toggle" aria-expanded={open}>
        <span className="flex items-center gap-3 min-w-0">
          <span className="w-10 h-10 rounded-xl bg-brand-emerald/10 flex items-center justify-center shrink-0"><Landmark className="w-5 h-5 text-brand-emerald" /></span>
          <span className="text-left min-w-0">
            <span className="font-heading font-semibold text-brand-ink block leading-tight">Get Home Loan</span>
            <span className="text-xs text-brand-muted">View Offers</span>
          </span>
        </span>
        <ChevronDown className={`w-5 h-5 text-brand-muted shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-4 space-y-3" data-testid="sidebar-loan-list">
          <div className="bg-secondary rounded-xl p-3" data-testid="sidebar-emi">
            <div className="text-xs font-semibold text-brand-ink mb-2 flex items-center gap-1.5"><Calculator className="w-3.5 h-3.5 text-brand-emerald" /> Quick EMI estimate</div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] text-brand-muted block">Amount ₹</label>
                <input type="number" value={emiAmt} onChange={(e) => setEmiAmt(e.target.value)} data-testid="sidebar-emi-amount" className="w-full mt-0.5 rounded-lg border border-brand-line bg-card px-2 py-1 text-xs" />
              </div>
              <div>
                <label className="text-[10px] text-brand-muted block">Rate %</label>
                <input type="number" value={emiRate} onChange={(e) => setEmiRate(e.target.value)} data-testid="sidebar-emi-rate" className="w-full mt-0.5 rounded-lg border border-brand-line bg-card px-2 py-1 text-xs" />
              </div>
              <div>
                <label className="text-[10px] text-brand-muted block">Years</label>
                <input type="number" value={emiYears} onChange={(e) => setEmiYears(e.target.value)} data-testid="sidebar-emi-years" className="w-full mt-0.5 rounded-lg border border-brand-line bg-card px-2 py-1 text-xs" />
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between bg-brand-emerald/10 rounded-lg px-3 py-2">
              <span className="text-xs text-brand-muted">Est. monthly EMI</span>
              <span className="font-heading font-bold text-brand-emerald" data-testid="sidebar-emi-result">{emi ? `${formatPrice(Math.round(emi))}/mo` : "—"}</span>
            </div>
          </div>
          {list.map((p) => (
            <div key={p.id} className="bg-secondary rounded-xl p-3" data-testid={`sidebar-loan-provider-${p.id}`}>
              <div className="flex items-center gap-3">
                {p.logo
                  ? <img src={fileUrl(p.logo)} alt={p.name} className="w-11 h-11 rounded-lg object-contain bg-white border border-brand-line shrink-0" />
                  : <div className="w-11 h-11 rounded-lg bg-brand-emerald/10 flex items-center justify-center shrink-0"><Landmark className="w-5 h-5 text-brand-emerald" /></div>}
                <div className="min-w-0">
                  <div className="font-semibold text-brand-ink text-sm truncate flex items-center gap-1.5">
                    {p.name}
                    {p.featured && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full shrink-0"><Star className="w-2.5 h-2.5 fill-current" />Featured</span>}
                  </div>
                  {p.interest_rate && <div className="text-xs text-brand-emerald font-medium">Interest {p.interest_rate}</div>}
                </div>
              </div>
              {(p.contact || p.website) && (
                <div className="mt-3 flex items-center gap-2">
                  {p.contact && (
                    <button onClick={() => enquire(p)} data-testid={`sidebar-loan-wa-${p.id}`}
                      className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-brand-lease hover:bg-brand-lease/90 text-white text-sm font-medium py-2 transition-colors">
                      <MessageCircle className="w-4 h-4" /> WhatsApp
                    </button>
                  )}
                  {p.website && (
                    <a href={p.website} target="_blank" rel="noopener noreferrer" data-testid={`sidebar-loan-site-${p.id}`}
                      className="flex-1 flex items-center justify-center gap-1.5 rounded-full border border-brand-emerald text-brand-emerald hover:bg-brand-emerald/5 text-sm font-medium py-2 transition-colors">
                      <Globe className="w-4 h-4" /> Visit site
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
          {usePaging && !showAll && (
            <button onClick={() => setShowAll(true)} data-testid="sidebar-loan-view-all" className="w-full text-sm font-medium text-brand-emerald hover:underline py-1">
              View All Providers ({sorted.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
}
